<template lang="pug">
.room-table-component
  // テーブル
  b-table(
    :items="_value"
    :fields="fields"
    :current-page="currentPage"
    :per-page="perPage"
    :filter="filter"
    :busy="isLoading"
    sticky-header
    no-border-collapse
    table-class="basic-table"
    show-empty
    :filter-function="filterTable"
    @filtered="onFiltered"
  )
    // ローディング
    template(#table-busy)
      .text-center.table-loading-message
        b-spinner.spinner
        span.message.align-self-center 読込み中...
    // レコード無し
    template(#empty)
      .empty-message
        span 情報がありません
    // フィルター後レコード無し
    template(#emptyfiltered)
      .empty-message
        span 情報がありません

    // セル：用途
    template(#cell(useType)="row")
      span {{ getUseType(row.item.useType) }}
    // セル：部屋種別
    template(#cell(roomType)="row")
      span {{ getRoomType(row.item.roomType) }}
    // セル：部屋定員
    template(#cell(capacity)="row")
      template(v-if="row.item.roomType != 3 && row.item.roomType != 4")
        span －
      template(v-else)
        span {{ row.item.capacity }}
    // セル：登録情報
    template(#cell(operation)="row")
      .control-buttons
        button.btn.edit-btn(@click="onEdit(row.item)" type="button") 修正
        button.btn.delete-btn(@click="onDelete(row.item)" type="button") 削除
</template>

<script lang="ts">
import { Component, Prop, Vue } from "vue-property-decorator";
import { UseType, RoomType } from "@/commons/services/Rooms";

/**
 * ### 部屋テーブルコンポーネント
 */
@Component({})
export default class RoomTable extends Vue {
  /** 1行あたりの情報配列 */
  @Prop({ required: true })
  value!: Array<RoomTableItem>;
  /** 列名配列 */
  @Prop({ required: true })
  fields!: Array<RoomTableField>;
  /** 全体行数 */
  @Prop({ required: true })
  totalRows!: number;
  /** 現在のページ */
  @Prop({ required: true })
  currentPage!: number;
  /** 1ページあたりの表示行数 */
  @Prop({ required: true })
  perPage!: number;
  /** ローディング表示可否 */
  @Prop({ required: true })
  isLoading!: boolean;
  /** フィルター */
  @Prop()
  filter!: FilterSetting;

  get _value(): Array<RoomTableItem> {
    return this.value;
  }

  /**
   * ### フィルタリング
   */
  public filterTable(row: RoomTableItem, filter: FilterSetting): boolean {
    return this.filterGroupId(row, filter) && this.filterSearchWords(row, filter);
  }

  public filterGroupId(row: RoomTableItem, filter: FilterSetting): boolean {
    if (filter.residentGroupId == "") {
      return true;
    }

    if (row.residentGroupId == Number(filter.residentGroupId)) {
      return true;
    } else {
      return false;
    }
  }

  public filterSearchWords(row: RoomTableItem, filter: FilterSetting): boolean {
    if (filter.searchWord == "") {
      return true;
    }

    const words: string[] = this.$filterUtil.createSearchWord(filter.searchWord);

    let foundRoom: boolean = true;

    for (const word of words) {
      if (row.name.indexOf(word) == -1) {
        foundRoom = false;
      }
    }

    return foundRoom;
  }

  /**
   * ### フィルタリングイベント
   */
  public onFiltered(filteredItems: any) {
    // Trigger pagination to update the number of buttons/pages due to filtering
    this.currentPage = 1;
    this.$emit("update:totalRows", filteredItems.length);
    return;
  }

  /**
   * ### 用途名を返す
   */
  public getUseType(useType: UseType): string {
    if (useType == UseType.UNMANAGED) {
      return "空床管理対象外";
    } else if (useType == UseType.NORMAL_STAY) {
      return "入居用部屋";
    } else if (useType == UseType.SHORT_STAY) {
      return "ショートステイ";
    } else {
      return "";
    }
  }

  /**
   * ### 部屋種別名を返す
   */
  public getRoomType(roomType: RoomType): string {
    if (roomType == RoomType.UNMANAGED) {
      return "空床管理対象外";
    } else if (roomType == RoomType.SINGLE) {
      return "個室";
    } else if (roomType == RoomType.DOUBLE) {
      return "２人部屋（夫婦部屋）";
    } else if (roomType == RoomType.MULTI_MEN) {
      return "多床室（男性）";
    } else if (roomType == RoomType.MULTI_WOMEN) {
      return "多床室（女性）";
    } else {
      return "";
    }
  }

  /**
   * ### 修正ボタン押下イベント
   */
  public onEdit(family: RoomTableItem): void {
    this.$emit("onEdit", family);
    return;
  }

  /**
   * ### 削除ボタン押下イベント
   */
  public onDelete(family: RoomTableItem): void {
    this.$emit("onDelete", family);
    return;
  }
}

/** テーブルの列名の情報 */
export interface RoomTableField {
  key: string;
  label: string;
  sortable: boolean;
  sortDirection: string;
  class?: string;
}

/** テーブルの1行あたりの情報 */
export interface RoomTableItem {
  id: number;
  name: string;
  useType: UseType;
  roomType: RoomType;
  capacity: number;
  residentGroupId: number;
  residentGroupName: string;
  updated: number;
}

export interface FilterSetting {
  residentGroupId: string;
  searchWord: string;
}
</script>

<style lang="scss" scoped>
.room-table-component {
  .edit-btn,
  .delete-btn {
    width: 64px;
  }

  .edit-btn {
    margin-right: 12px;
  }
}
</style>
